import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Bookmark, BookmarkCheck, X, Search, Cpu, Settings, HeartPulse, Scale, TrendingUp, Calculator, Trophy, Building2, GraduationCap, Palette, Brain, LineChart, Briefcase, Film, FlaskConical, Rocket } from "lucide-react";

const ICONS = {
  cpu: Cpu, settings: Settings, "heart-pulse": HeartPulse, scale: Scale, "trending-up": TrendingUp,
  calculator: Calculator, trophy: Trophy, "building-2": Building2, "graduation-cap": GraduationCap,
  palette: Palette, brain: Brain, "line-chart": LineChart, briefcase: Briefcase, film: Film,
  "flask-conical": FlaskConical, rocket: Rocket,
};

const CATEGORIES = ["All", "Engineering", "Medical", "Law", "Commerce", "Sports", "Architecture", "Teaching", "Design & Arts"];

export default function Careers() {
  const [careers, setCareers] = useState([]);
  const [saved, setSaved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [params, setParams] = useSearchParams();

  const loadAll = async () => {
    const [c, s] = await Promise.all([
      api.get("/careers/", { params: { category: cat === "All" ? undefined : cat, q: q || undefined } }),
      api.get("/careers/saved/list").catch(() => ({ data: [] })),
    ]);
    setCareers(c.data);
    setSaved(s.data.map((x) => x.career_id));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAll().finally(() => setLoading(false)); }, [cat]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(loadAll, 300); return () => clearTimeout(t); }, [q]);

  useEffect(() => {
    const openId = params.get("open");
    if (openId && careers.length) {
      const found = careers.find((c) => c.id === openId);
      if (found) setOpen(found);
    }
  }, [params, careers]);

  const toggleSave = async (c) => {
    try {
      if (saved.includes(c.id)) {
        await api.delete(`/careers/save/${c.id}`);
        setSaved(saved.filter((x) => x !== c.id));
        toast.success(`Removed ${c.name}`);
      } else {
        await api.post("/careers/save", { career_id: c.id });
        setSaved([...saved, c.id]);
        toast.success(`Saved ${c.name}`);
      }
    } catch { toast.error("Action failed"); }
  };

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-6 py-10" data-testid="careers-page">
        <p className="text-[11px] uppercase tracking-[0.2em] text-yellow-300">Explore</p>
        <h1 className="font-display text-4xl md:text-5xl text-white mt-2">Career Fields</h1>
        <p className="text-slate-400 mt-2 max-w-xl">Discover paths across engineering, medical, law, commerce, sports, arts & more.</p>

        {/* Filters */}
        <div className="mt-8 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 border border-white/10 bg-white/5 rounded-full px-4 py-2 min-w-[260px]">
            <Search className="w-4 h-4 text-slate-400" />
            <input data-testid="careers-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search fields…" className="bg-transparent outline-none text-white text-sm w-full placeholder:text-slate-400" />
          </div>
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map((c) => (
              <button key={c} data-testid={`career-cat-${c}`}
                onClick={() => setCat(c)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border transition ${cat === c ? "border-yellow-400 text-slate-900" : "border-white/10 text-slate-300 hover:bg-white/5"}`}
                style={cat === c ? { background: "#FBBF24" } : {}}>
                {c}
              </button>
            ))}
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="mt-8 text-slate-300 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {careers.map((c) => {
              const Icon = ICONS[c.icon] || Cpu;
              return (
                <div key={c.id} data-testid={`career-card-${c.id}`} className="brimi-card p-5 flex flex-col cursor-pointer" onClick={() => setOpen(c)}>
                  <div className="flex items-start justify-between">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: "#EEF2FF", color: "#4338CA" }}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <button data-testid={`save-career-${c.id}`} onClick={(e) => { e.stopPropagation(); toggleSave(c); }} className="text-slate-400 hover:text-coral-500" style={{ color: saved.includes(c.id) ? "#FB7185" : undefined }}>
                      {saved.includes(c.id) ? <BookmarkCheck className="w-5 h-5" /> : <Bookmark className="w-5 h-5" />}
                    </button>
                  </div>
                  <p className="font-display text-lg text-slate-900 mt-4">{c.name}</p>
                  <p className="text-sm text-slate-500 mt-1 line-clamp-2">{c.tagline}</p>
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-bold">{c.category}</span>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 font-bold">💰 {c.avg_salary_india}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Detail modal */}
        {open && (
          <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setOpen(null)} data-testid="career-modal">
            <div className="brimi-card p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto relative" onClick={(e) => e.stopPropagation()}>
              <button onClick={() => setOpen(null)} data-testid="career-modal-close" className="absolute top-4 right-4 text-slate-500 hover:text-slate-900"><X className="w-5 h-5" /></button>
              <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">{open.category}</p>
              <h2 className="font-display text-3xl text-slate-900 mt-1">{open.name}</h2>
              <p className="text-slate-700 mt-2">{open.tagline}</p>
              <div className="grid sm:grid-cols-2 gap-4 mt-5 text-sm">
                <Info label="Streams">{(open.streams || []).join(", ")}</Info>
                <Info label="Key subjects">{(open.key_subjects || []).join(", ")}</Info>
                <Info label="Entrance (India)">{(open.entrance_exams_india || []).join(", ")}</Info>
                <Info label="Entrance (Global)">{(open.entrance_exams_global || []).join(", ")}</Info>
                <Info label="Avg. Salary India">{open.avg_salary_india}</Info>
                <Info label="Avg. Salary Global">{open.avg_salary_global}</Info>
              </div>
              <div className="mt-5">
                <p className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-bold">Top roles</p>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {(open.top_roles || []).map((r) => <span key={r} className="text-xs px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 font-bold">{r}</span>)}
                </div>
              </div>
              <div className="mt-5 p-4 rounded-xl bg-yellow-50 border border-yellow-200">
                <p className="text-xs font-bold text-yellow-800 uppercase tracking-[0.15em]">Why it&apos;s growing</p>
                <p className="text-slate-800 mt-1 text-sm">{open.why}</p>
              </div>
              <div className="mt-6 flex gap-2">
                <button data-testid="career-modal-save" onClick={() => toggleSave(open)} className="brimi-pill accent-coral flex items-center gap-2">
                  {saved.includes(open.id) ? <><BookmarkCheck className="w-4 h-4" /> Saved</> : <><Bookmark className="w-4 h-4" /> Save this career</>}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}

function Info({ label, children }) {
  return (
    <div className="p-3 rounded-xl bg-slate-50 border border-slate-100">
      <p className="text-[11px] uppercase tracking-[0.15em] text-slate-500 font-bold">{label}</p>
      <p className="text-slate-800 mt-0.5">{children}</p>
    </div>
  );
}
